import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsList = ({ jobs, onViewBids, onRefresh }) => {
  const [deletingJob, setDeletingJob] = useState(null);

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this cancelled job? This action cannot be undone.')) {
      return;
    }
    
    setDeletingJob(jobId);
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete job');
    } finally {
      setDeletingJob(null);
    }
  };

  if (jobs.length === 0) return null;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-testid="my-jobs-section">
      <h2 className="text-xl font-bold mb-4">My Posted Jobs</h2>
      <div className="space-y-4">
        {jobs.map((job) => {
          const isCompleted = job.status === 'completed';
          const isCancelled = job.status === 'cancelled';
          const isAbandoned = job.status === 'abandoned';
          const isEditable = !isCompleted && !isCancelled && !isAbandoned;
          const isDeletable = isCancelled || isAbandoned;
          
          return (
            <div
              key={job.id}
              className="border border-gray-200 rounded-lg p-4"
              data-testid={`job-item-${job.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-800">{job.title}</h3>
                    {job.job_order_code && (
                      <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        #JO-{job.job_order_code}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{job.category}</p>
                  <p className="text-gray-900 font-semibold">
                    Budget: ₱{job.budget?.toLocaleString()}
                  </p>
                  
                  {/* Show bid count with link to view bids */}
                  {job.total_bids > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => onViewBids(job)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                        data-testid={`view-bids-${job.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                        </svg>
                        View & Approve ({job.total_bids} {job.total_bids === 1 ? 'bid' : 'bids'})
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    job.status === 'open' ? 'bg-green-100 text-green-700' :
                    job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    job.status === 'completed' ? 'bg-green-100 text-green-700' :
                    job.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    job.status === 'abandoned' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {job.status?.replace('_', ' ')}
                  </span>
                  
                  <div className="flex items-center gap-3">
                    {/* Edit button */}
                    {isEditable ? (
                      <Link
                        to={`/edit-job/${job.id}`}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        data-testid={`edit-job-${job.id}`}
                      >
                        ✏️ Edit
                      </Link>
                    ) : (
                      <span 
                        className="text-gray-400 text-sm cursor-not-allowed"
                        title={isCompleted ? "Cannot edit completed jobs" : "Cannot edit cancelled/abandoned jobs"}
                      >
                        ✏️ Edit
                      </span>
                    )}
                    
                    {/* Delete button for cancelled/abandoned jobs */}
                    {isDeletable && (
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        disabled={deletingJob === job.id}
                        className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                        data-testid={`delete-job-${job.id}`}
                        title="Delete this cancelled job"
                      >
                        {deletingJob === job.id ? '⏳' : '🗑️'} Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobsList;
